import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import SalesText from '../../components/_atoms/SalesText/SalesText'
import { colors, fontSizes, fontWeights } from '../../constants/salesStyles'
import { useGetAnalyticsPartsAddedMutation } from '../../modules/store/datahubApi/datahubApi'
import { useGetAnalyticsDealsMutation } from '../../modules/store/dochubApi/dochubApi'
import { DealStatus } from '../../modules/store/dochubApi/dochubTypes'
import { adminRouteAlias, getAdminNavigationPath } from '../../router/adminRouteAlias'
import SalesButtonStandard from '../../components/_molecules/SalesButtonStandard/SalesButtonStandard'

import styles from './styles.module.scss'

const getMonthKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`

const toDate = (value: string) => new Date(`${value}T00:00:00`)

const formatAxisDate = (value: string) => {
    const date = toDate(value)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${day}.${month}`
}

const buildMonthPeriod = (monthValue: string) => {
    const [year, month] = monthValue.split('-').map(Number)
    if (!year || !month) return null
    const daysInMonth = new Date(year, month, 0).getDate()
    return {
        from: `${year}-${String(month).padStart(2, '0')}-01`,
        to: `${year}-${String(month).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`,
    }
}

const AnalyticsDashboard = () => {
    const navigate = useNavigate()
    const [getAnalyticsPartsAdded, { data: analyticsPartsData, isLoading: isAnalyticsPartsLoading }] =
        useGetAnalyticsPartsAddedMutation()
    const [getAnalyticsDeals, { data: analyticsDealsData, isLoading: isAnalyticsDealsLoading }] =
        useGetAnalyticsDealsMutation()
    const currentCalendarMonthKey = useMemo(() => getMonthKey(new Date()), [])
    const monthPeriod = useMemo(() => buildMonthPeriod(currentCalendarMonthKey), [currentCalendarMonthKey])

    useEffect(() => {
        if (!monthPeriod) return
        getAnalyticsPartsAdded({
            period: monthPeriod,
            manager: [],
        })
        getAnalyticsDeals({
            period: monthPeriod,
            status: [DealStatus.issued],
            marketplace: [],
            dateType: 'created',
        })
    }, [getAnalyticsDeals, getAnalyticsPartsAdded, monthPeriod])

    const partsByDay = analyticsPartsData?.data?.chartByDay || []
    const partsAddedCount = analyticsPartsData?.data?.totals?.addedCount || 0
    const dealsByDay = analyticsDealsData?.data?.chartByDay || []
    const dealsClosedCount = analyticsDealsData?.data?.totals?.dealsCount || 0
    const isPartsLoading = isAnalyticsPartsLoading && partsByDay.length === 0
    const isDealsLoading = isAnalyticsDealsLoading && dealsByDay.length === 0

    return (
        <div className={styles.wrap}>
            <SalesText text="Аналитика" fontSize={fontSizes.xxl} fontWeight={fontWeights.bold} />
            <div className={styles.section}>
                <div className={styles.kpiGrid}>
                    <div className={styles.kpiCard}>
                        <div className={styles.kpiInfo}>
                            <div className={styles.kpiLabel}>Добавлено запчастей</div>
                            {isPartsLoading ? (
                                <div className={`${styles.skeletonPulse} ${styles.skeletonKpiValue}`} />
                            ) : (
                                <div className={styles.kpiValue}>{partsAddedCount}</div>
                            )}
                            <div className={styles.kpiSubtext}>за текущий месяц</div>
                        </div>
                        <div className={styles.kpiAction}>
                            <SalesButtonStandard
                                text="Аналитика по запчастям"
                                onClick={() => navigate(getAdminNavigationPath(adminRouteAlias.analytics.parts))}
                            />
                        </div>
                    </div>
                    <div className={styles.kpiCard}>
                        <div className={styles.kpiInfo}>
                            <div className={styles.kpiLabel}>Закрыто сделок</div>
                            {isDealsLoading ? (
                                <div className={`${styles.skeletonPulse} ${styles.skeletonKpiValue}`} />
                            ) : (
                                <div className={styles.kpiValue}>{dealsClosedCount}</div>
                            )}
                            <div className={styles.kpiSubtext}>за текущий месяц</div>
                        </div>
                        <div className={styles.kpiAction}>
                            <SalesButtonStandard
                                text="Аналитика по сделкам"
                                onClick={() => navigate(getAdminNavigationPath(adminRouteAlias.analytics.deals))}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.sectionTitle}>Добавленные запчасти за текущий месяц</div>
                <div className={styles.chart}>
                    {isPartsLoading ? (
                        <div className={`${styles.skeletonPulse} ${styles.skeletonChart}`} />
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={partsByDay}>
                                <CartesianGrid vertical={false} />
                                <XAxis dataKey="date" interval={0} tickFormatter={formatAxisDate} />
                                <YAxis allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'rgba(96, 97, 108, 0.85)',
                                        borderRadius: 10,
                                        border: 'none',
                                        color: colors.white,
                                    }}
                                    itemStyle={{ color: colors.white }}
                                    labelStyle={{ color: colors.white }}
                                />
                                <Bar dataKey="count" fill={colors.blueLight} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.sectionTitle}>Закрытые сделки за текущий месяц</div>
                <div className={styles.chart}>
                    {isDealsLoading ? (
                        <div className={`${styles.skeletonPulse} ${styles.skeletonChart}`} />
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={dealsByDay}>
                                <CartesianGrid vertical={false} />
                                <XAxis dataKey="date" interval={0} tickFormatter={formatAxisDate} />
                                <YAxis allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'rgba(96, 97, 108, 0.85)',
                                        borderRadius: 10,
                                        border: 'none',
                                        color: colors.white,
                                    }}
                                    itemStyle={{ color: colors.white }}
                                    labelStyle={{ color: colors.white }}
                                />
                                <Bar dataKey="count" fill={colors.grayDark} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
        </div>
    )
}

export default AnalyticsDashboard
